import {IconButton, showNotification, Tooltip, useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNil} from 'ramda';

import {suffixTestId, type TestIdProps} from 'shared/web';

import {PdfExportDialog, type PdfExportResult} from 'features/pdf-export';

import {useDataGridContext} from '../../context/useDataGridContext';
import {selectPdfExportUnavailableReason} from '../../store/selectors/selectPdfExportUnavailableReason';
import {useEagGridSelector} from '../../store/useEagGridSelector';
import {useEagGridStore} from '../../store/useEagGridStore';
import {captureGridPdfExport} from '../../utils/captureGridPdfExport';
import {createGridPdfExportJob} from '../../utils/createGridPdfExportJob';

type ExportJob = {
  title: string;
  count: number;
  create: (signal: AbortSignal) => Promise<PdfExportResult>;
};

export function GridPdfExportButton(props: TestIdProps) {
  const t = useTranslationContext();
  const context = useDataGridContext();
  const pdfColumns = context?.pdfColumns;
  const onPdfExportCallback = context?.onPdfExportCallback;
  const store = useEagGridStore();
  const reason = useEagGridSelector(selectPdfExportUnavailableReason);
  const [job, setJob] = useState<ExportJob | null>(null);
  if (isNil(pdfColumns) || isNil(onPdfExportCallback)) {
    return null;
  }

  const label = t('general.actions.generatePDF');
  const tooltip = match(reason)
    .with('empty', () => t('general.notifications.noItemsSelected'))
    .with('loading', () => t('general.notifications.dataStillLoadingTryAgain'))
    .with(
      'all-filtered',
      () => `${t('page.datagrid.labels.selectAll')}: ${t('general.label.notImplementedYet')}`
    )
    .with('disabled', () => t('general.notifications.noPermission'))
    .with('incomplete', 'unavailable', () => t('module.reportDocument.labels.dataUnavailable'))
    .with(null, () => label)
    .exhaustive();

  const startExport = () => {
    try {
      const request = captureGridPdfExport(store.getState(), pdfColumns);
      setJob({
        title: t('general.actions.exportPDF'),
        count: request.recordIds.length,
        create: createGridPdfExportJob(request, onPdfExportCallback),
      });
    } catch {
      showNotification.error(t('module.reportDocument.labels.dataUnavailable'), {
        layout: 'detailed',
        secondaryMessage: t('entity.integration.labels.pleaseRefresh'),
      });
    }
  };

  return (
    <>
      <Tooltip label={tooltip}>
        <span tabIndex={isNil(reason) ? undefined : 0} aria-label={tooltip}>
          <IconButton
            aria-label={label}
            icon="files_default/file_type_PDF_monochrome"
            iconSize="large"
            isDisabled={!isNil(reason) || !isNil(job)}
            onClick={startExport}
            data-testid={suffixTestId('pdfExportButton', props)}
          />
        </span>
      </Tooltip>
      <Show when={job}>
        {(activeJob) => (
          <PdfExportDialog
            title={activeJob.title}
            description={t('page.datagrid.labels.numberOfSelectedRows', {
              selectedRows: activeJob.count,
            })}
            create={activeJob.create}
            onClose={() => setJob(null)}
          />
        )}
      </Show>
    </>
  );
}
